/**
 * PublicStartPage — `/start`
 * --------------------------
 * The landing page's big call to action drops people here, and this is the one
 * client screen that is NOT behind the auth guard.
 *
 * It is deliberately the same screen as `/start-consultation`, with one
 * addition: the phone/OTP panel sits inline. Nobody is bounced to a login page
 * before they have been told what they are getting — the sign-in happens in
 * place, and the app fills in around them.
 *
 * Three outcomes after a correct code:
 *   - existing client  -> signed in silently, the quick bar comes alive, the
 *                         page stays exactly where it is
 *   - new phone        -> a pre-signup guest (guest_########). They may capture
 *                         and see the free preview; the profile is only needed
 *                         at payment
 *   - wrong code       -> stay put, show the error
 */
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Stack,
  Card,
  CardContent,
  Button,
  TextField,
  Alert,
  Chip,
} from '@mui/material';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import VerifiedIcon from '@mui/icons-material/Verified';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import Container from '@/components/PageSheet';
import { authApi, consultationsApi } from '@/services/api';
import { loginSuccess } from '@/store/authSlice';
import { toE164, isValidIranianMobile } from '@/utils/phone';

const OTP_LENGTH = 5;

export default function PublicStartPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isAuthenticated = useSelector((s) => s.auth.isAuthenticated);

  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');
  const [stage, setStage] = useState('phone'); // phone | code | done
  const [error, setError] = useState('');
  const [isGuest, setIsGuest] = useState(false);

  const termsQuery = useQuery({
    queryKey: ['consultation-terms'],
    queryFn: () => consultationsApi.getConsultationTerms(),
    staleTime: 5 * 60 * 1000,
  });
  const terms = termsQuery.data || {};

  const sendMut = useMutation({
    mutationFn: () => authApi.requestOtp(toE164(phone)),
    onSuccess: () => { setError(''); setStage('code'); },
    onError: (err) =>
      setError(err?.response?.data?.error?.message || t('common.error')),
  });

  const verifyMut = useMutation({
    mutationFn: () => authApi.verifyOtp(toE164(phone), code),
    onSuccess: (res) => {
      setError('');
      // Sign in WITHOUT navigating: the point of this screen is that the app
      // fills in around the visitor rather than throwing them to a login page.
      dispatch(loginSuccess({ accessToken: res.accessToken, user: res.user || null }));
      setIsGuest(Boolean(res.isNewUser));
      setStage('done');
    },
    onError: (err) =>
      setError(err?.response?.data?.error?.message || t('common.error')),
  });

  const startMut = useMutation({
    mutationFn: () => consultationsApi.startConsultation(),
    onSuccess: (c) => navigate(`/face-capture?consultation=${encodeURIComponent(c.id)}`),
    onError: (err) =>
      setError(err?.response?.data?.error?.message || t('common.error')),
  });

  const verified = stage === 'done' || isAuthenticated;

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      <Typography variant="h6" fontWeight={800}>{t('startConsultation.title')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('startConsultation.subtitle')}
      </Typography>

      <Card elevation={0} sx={{ mb: 2, borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
        <CardContent>
          <Stack spacing={1.25}>
            <Promise icon={<PhotoCameraIcon fontSize="small" />} text={t('startConsultation.promiseFree')} />
            <Promise icon={<VerifiedIcon fontSize="small" />}
              text={t('startConsultation.promiseSla', { hours: terms.slaHours ?? 48 })} />
            <Promise icon={<ChatBubbleOutlineIcon fontSize="small" />}
              text={t('startConsultation.promiseChat', { days: terms.chatDays ?? 7 })} />
          </Stack>
        </CardContent>
      </Card>

      {/* ---- inline sign-in ---- */}
      {!verified ? (
        <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
          <CardContent>
            <Typography fontWeight={800}>{t('publicStart.verifyTitle')}</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, mb: 2 }}>
              {t('publicStart.verifyBody')}
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {stage === 'phone' ? (
              <Stack spacing={1.5}>
                <TextField
                  fullWidth
                  label={t('auth.phoneLabel')}
                  placeholder={t('auth.phonePlaceholder')}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  inputMode="tel"
                  autoComplete="tel"
                />
                <Button
                  fullWidth size="large" variant="contained"
                  disabled={!isValidIranianMobile(phone) || sendMut.isPending}
                  onClick={() => sendMut.mutate()}
                >
                  {sendMut.isPending ? t('auth.sendingOtp') : t('auth.sendOtp')}
                </Button>
              </Stack>
            ) : (
              <Stack spacing={1.5}>
                <TextField
                  fullWidth
                  label={t('auth.otpLabel')}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH))}
                  inputMode="numeric"
                  autoComplete="one-time-code"
                />
                <Button
                  fullWidth size="large" variant="contained"
                  disabled={code.length !== OTP_LENGTH || verifyMut.isPending}
                  onClick={() => verifyMut.mutate()}
                >
                  {verifyMut.isPending ? t('common.loading') : t('auth.verifyOtp')}
                </Button>
                <Button size="small" onClick={() => { setStage('phone'); setCode(''); setError(''); }}>
                  {t('auth.changePhone')}
                </Button>
              </Stack>
            )}
          </CardContent>
        </Card>
      ) : (
        <Stack spacing={2}>
          <Alert
            severity="success"
            icon={<CheckCircleIcon fontSize="inherit" />}
            sx={{ borderRadius: 3 }}
          >
            {isGuest ? t('publicStart.guestWelcome') : t('publicStart.welcomeBack')}
            {isGuest && (
              <Chip size="small" sx={{ ms: 1 }} label={t('publicStart.guestChip')} />
            )}
          </Alert>

          <Button
            fullWidth size="large" variant="contained"
            startIcon={<PhotoCameraIcon />}
            disabled={startMut.isPending}
            onClick={() => startMut.mutate()}
            sx={{ py: 1.5, fontSize: '1rem', fontWeight: 800, borderRadius: 3 }}
          >
            {startMut.isPending ? t('common.loading') : t('startConsultation.cta')}
          </Button>
          <Typography variant="caption" color="text.secondary" align="center">
            {t('startConsultation.ctaHint')}
          </Typography>
        </Stack>
      )}
    </Container>
  );
}

function Promise({ icon, text }) {
  return (
    <Stack direction="row" spacing={1.25} alignItems="flex-start">
      <Box sx={{ color: 'secondary.main', mt: '2px', display: 'flex' }}>{icon}</Box>
      <Typography variant="body2" sx={{ lineHeight: 1.7 }}>{text}</Typography>
    </Stack>
  );
}
